import _ from 'lodash';
import { Observable } from 'rxjs';
import { Store } from 'redux';
import { ActionsObservable } from 'redux-observable';
import { startSubmit, stopSubmit, initialize } from 'redux-form/immutable';
import { replace, push } from 'react-router-redux';

import CauseApi from 'entities/cause/api';
import Cause from 'entities/cause/model';
import { selectCauseById } from 'entities/cause/selectors';
import { selectUser } from 'entities/user/selectors';
import { showMessageAction, showErrorAction } from 'containers/Notification/actions';

import { FORM_IDENTIFIER, INIT, SAVE, DUPLICATE, REMOVE } from './constants';
import {
  InitAction,
  initSuccessAction,
  initFailedAction,
  SaveAction,
  saveSuccessAction,
  saveFailedAction,
  DuplicateAction,
  RemoveAction,
  removeSuccessAction,
  removeFailedAction,
} from './actions';

const init = (action$: ActionsObservable<InitAction>, store: Store) =>
  action$.ofType(INIT).mergeMap(({ data: { id = '', cause } }: InitAction | any) => {
    const state = store.getState();
    const actions = [];
    const user = selectUser(state);

    if (!user) {
      return [];
    }

    if (id === 'new') {
      const newCause = new Cause({ ...cause });
      const initAction = initialize(FORM_IDENTIFIER, newCause);
      actions.push(Observable.of(initAction));
    } else {
      const causeFromSelector = selectCauseById(state, id);
      if (causeFromSelector?.nonprofits) {
        actions.push(Observable.of(initialize(FORM_IDENTIFIER, causeFromSelector)));
      } else {
        actions.push(
          CauseApi.fetch(id)
            .map((action: { type: string; cause: Map<string, Cause> }) => {
              const fetchedCause = action.cause;
              return [action, initialize(FORM_IDENTIFIER, fetchedCause)];
            })
            .catch((e: Error) => Observable.of([initFailedAction(e), replace('/notFound')]))
        );
      }
    }

    return Observable.combineLatest(actions)
      .mergeMap((resultingActions) => [..._.flatten(resultingActions), initSuccessAction()])
      .catch((e) => Observable.of(initFailedAction(e)));
  });

const save = (action$: ActionsObservable<SaveAction>) =>
  action$.ofType(SAVE).mergeMap((payload: { data: Cause }) =>
    Observable.concat(
      Observable.of(startSubmit(FORM_IDENTIFIER)),
      Observable.from(
        payload.data.id
          ? CauseApi.update(payload.data.id, payload.data)
          : CauseApi.create(payload.data)
      )
        .mergeMap((action: { type: string; cause: Cause }) => [
          action,
          saveSuccessAction(),
          stopSubmit(FORM_IDENTIFIER),
          initialize(FORM_IDENTIFIER, action.cause),
          replace(`/causes/edit/${action.cause.id}`),
          showMessageAction(payload.data.id ? 'Changes Saved!' : 'Cause Area Created!'),
        ])
        .catch((e: Error) =>
          Observable.of(
            saveFailedAction(e, 'saveFailed'),
            stopSubmit(FORM_IDENTIFIER),
            showErrorAction('An error occurred during saving, please try again.')
          )
        )
    )
  );

const duplicate = (action$: ActionsObservable<DuplicateAction>) =>
  action$.ofType(DUPLICATE).mergeMap((payload: { data: Cause }) => [
    initialize(
      FORM_IDENTIFIER,
      new Cause({
        ...payload.data,
        id: undefined,
        __t: payload.data.type,
        name: `${payload.data.name} (copy)`,
      })
    ),
    replace('/causes/edit/new'),
  ]);

const remove = (action$: ActionsObservable<RemoveAction>) =>
  action$.ofType(REMOVE).mergeMap((payload) =>
    Observable.from(CauseApi.delete(payload.causeId))
      .mergeMap((action) => [
        action,
        removeSuccessAction(),
        push('/causes'),
        showMessageAction('Cause Area Removed!'),
      ])
      .catch((e) =>
        Observable.of(
          removeFailedAction(e, 'removeFailed'),
          stopSubmit(FORM_IDENTIFIER),
          showErrorAction('An error occurred during saving, please try again.')
        )
      )
  );

export default [init, save, duplicate, remove];
